import { createRoute, useNavigate } from "@tanstack/react-router";
import { Route as RootRoute } from "../../__root";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import {
  useCompleteUserInvitation,
  useUserInvitationToken,
} from "../../../hooks/useUserInviteOnboarding";
import { MicrogroupOnboardingStepper } from "../../../components/tenant/MicrogroupOnboardingStepper";
import type { MicrogroupOnboardingTokenInfo } from "../../../schemas/microgroupOnboarding";
import { ApiError } from "../../../lib/apiClient";
import { hydrateAuth } from "../../../lib/authStore";
import { renderConsentMarkdown } from "../../../lib/markdownRender";

/**
 * Admin-driven pozvánka — onboarding landing `/p/invite/{token}` (V069).
 *
 * Reuse [MicrogroupOnboardingStepper] (consent + heslo wizard) s obecným
 * nadpisem. Peek → stepper → complete → User + Holder + JWT → redirect.
 */

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/p/invite/$token",
  component: UserInvitationOnboardingLanding,
});

function UserInvitationOnboardingLanding() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const tokenQuery = useUserInvitationToken(token);
  const completeMutation = useCompleteUserInvitation();

  if (tokenQuery.isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-secondary px-4 py-12">
        <LoadingIndicator label="Ověřuji odkaz…" />
      </main>
    );
  }

  if (tokenQuery.isError || !tokenQuery.data) {
    const isExpired =
      tokenQuery.error instanceof ApiError &&
      (tokenQuery.error.status === 410 || tokenQuery.error.status === 404);
    return (
      <main
        className="min-h-screen bg-secondary px-4 py-12 md:py-16"
        aria-labelledby="inv-410-heading"
      >
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-5">
          <h1 id="inv-410-heading" className="text-xl font-semibold text-primary md:text-display-xs">
            {isExpired ? "Odkaz již nelze použít" : "Ověření odkazu selhalo"}
          </h1>
          <InlineAlert
            variant="warning"
            title={isExpired ? "Pozvánka vypršela nebo byla použita" : "Chyba"}
          >
            {isExpired
              ? "Pozvánka vypršela nebo už byla použita. Požádejte správce o novou."
              : "Zkuste odkaz otevřít znovu. Pokud problém přetrvává, kontaktujte podporu."}
          </InlineAlert>
        </div>
      </main>
    );
  }

  // Map peek response na stepper tokenInfo (microgroup_name nevyužito — nahrazeno
  // obecným welcomeHeading).
  const peek = tokenQuery.data;
  const tokenInfo: MicrogroupOnboardingTokenInfo = {
    invitation_id: peek.invitation_id,
    invited_name: peek.invited_name,
    invited_email: peek.invited_email,
    microgroup_name: "",
    consent_text_version: peek.consent_text_version ?? "",
    // API vrací consent text jako raw markdown (jako body_markdown v běžném flow) →
    // vyrenderujeme na HTML; stepper ho pak sanitizuje (DOMPurify) a zobrazí.
    consent_text_html: peek.consent_text_html ? renderConsentMarkdown(peek.consent_text_html) : "",
    expires_at: peek.expires_at,
  };

  return (
    <main className="min-h-screen bg-secondary px-4 py-12 md:py-16">
      <div className="mx-auto w-full max-w-2xl">
      <MicrogroupOnboardingStepper
        tokenInfo={tokenInfo}
        welcomeHeading={`Vítejte v Axigonu, ${peek.invited_name}`}
        onSubmit={async (input) => {
          await completeMutation.mutateAsync({
            token,
            password: input.password,
            consent_text_version: input.consent_text_version,
            consent_types: input.consent_types,
          });
        }}
        onCompleted={async () => {
          await hydrateAuth();
          setTimeout(() => {
            navigate({ to: "/tenant/dashboard" });
          }, 1500);
        }}
      />
      </div>
    </main>
  );
}
